import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { productService } from '../../../../services/erpApi';
import './TVDisplayPage.css';

/** Tempo de exibição de cada produto em destaque (15 segundos) */
const ROTATION_MS = 15 * 1000;
const POLL_MS = 5 * 60 * 1000;

function formatPrice(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
}

function mapTvProduct(product) {
  return {
    id: product.id,
    name: product.name,
    price: product.price,
    image_url: product.image_url,
  };
}

function filterFeaturedProducts(products) {
  return (products || [])
    .filter(
      (p) =>
        p.is_featured === true &&
        p.active !== false &&
        p.image_url &&
        String(p.image_url).trim() !== ''
    )
    .map(mapTvProduct);
}

function TVDisplayPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [slideKey, setSlideKey] = useState(0);

  const loadProducts = useCallback(async () => {
    try {
      setLoadError(null);
      const res = await productService.getAll({ featured: true, active: true });
      if (res.success) {
        const featured = filterFeaturedProducts(res.data);
        setProducts(featured);
        setCurrentIndex((prev) => (featured.length === 0 ? 0 : prev % featured.length));
      } else {
        setLoadError(res.message || 'Não foi possível carregar os destaques.');
      }
    } catch (err) {
      console.error('Erro ao carregar vitrine TV:', err);
      setLoadError('Erro ao carregar produtos em destaque.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    const pollTimer = setInterval(loadProducts, POLL_MS);
    return () => clearInterval(pollTimer);
  }, [loadProducts]);

  useEffect(() => {
    const timeTimer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timeTimer);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    document.documentElement.classList.add('tv-display-kiosk');
    document.body.classList.add('tv-display-kiosk');
    return () => {
      document.documentElement.classList.remove('tv-display-kiosk');
      document.body.classList.remove('tv-display-kiosk');
    };
  }, []);

  useEffect(() => {
    if (products.length <= 1) return;

    const rotationTimer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % products.length);
      setSlideKey((k) => k + 1);
    }, ROTATION_MS);

    return () => clearInterval(rotationTimer);
  }, [products.length]);

  const currentProduct = products[currentIndex];

  const timeString = useMemo(
    () =>
      currentTime.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [currentTime]
  );

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  if (loading) {
    return (
      <div className="tv-display-loading">
        <p>Carregando vitrine…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="tv-display-empty">
        <p className="tv-display-empty-title">Vitrine indisponível</p>
        <p className="tv-display-empty-hint">{loadError}</p>
      </div>
    );
  }

  if (!currentProduct) {
    return (
      <div className="tv-display-empty">
        <p className="tv-display-empty-title">Nenhum produto em destaque</p>
        <p className="tv-display-empty-hint">
          Marque produtos como destaque no cadastro (ativos e com imagem).
        </p>
      </div>
    );
  }

  return (
    <div className="tv-display-root">
      <header className="tv-display-header">
        <div className="tv-display-brand">
          <span className="tv-display-logo">P</span>
          <h1>Pró Médico</h1>
        </div>

        <div className="tv-display-header-meta">
          <span className="tv-display-counter">
            {currentIndex + 1} / {products.length}
          </span>
          <time className="tv-display-clock">{timeString}</time>
          <button
            type="button"
            onClick={toggleFullscreen}
            className="tv-display-fullscreen-btn"
            title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
          >
            {isFullscreen ? <Minimize2 size={22} /> : <Maximize2 size={22} />}
          </button>
        </div>
      </header>

      <main className="tv-display-main" key={`${currentProduct.id}-${slideKey}`}>
        <div className="tv-display-image-wrap">
          <img
            src={currentProduct.image_url}
            alt={currentProduct.name}
            className="tv-display-image"
          />
        </div>

        <div className="tv-display-info">
          <h2 className="tv-display-name">{currentProduct.name}</h2>
          <p className="tv-display-price">{formatPrice(currentProduct.price)}</p>
        </div>
      </main>

      {products.length > 1 && (
        <div className="tv-display-dots" aria-hidden="true">
          {products.map((p, idx) => (
            <span
              key={p.id}
              className={`tv-display-dot ${idx === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TVDisplayPage;
